import Button from '@/components/UI/Button';
import FormField from '@/components/UI/FormFields/Latest/FormField';
import Header from '@/components/UI/Header';
import { StudyLookup } from '@/components/UI/StudyLookup/StudyLookup';
import type { components } from '@/generated/internal-api/openapi-types';
import type { StudyInfoV1 } from '@/model/Study.model';
import { AgentSessionService } from '@/support/AgentSessionService';
import { useAgentSession } from '@/support/Hooks/agent/useAgentSession';
import type { FC } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form-latest';
import { InVivoStudyHealthCheckResponse } from './InVivoStudyHealthCheckResponse';
import styles from './InVivoStudyHealthCheck.module.scss';

type StudyApiId = components['schemas']['StudyApiId.schema'];

interface StudyHealthCheckFormValues {
  study_api_id: StudyApiId | '';
}

/**
 * Admin-console screen that exercises the Study Health Check agent end-to-end:
 * picks a study, kicks off a session, and renders the streaming response
 * as it arrives over SSE.
 */
export const InVivoStudyHealthCheck: FC = () => {
  const formMethods = useForm<StudyHealthCheckFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      study_api_id: '',
    },
  });

  const { state, createSession, cancel, reset } = useAgentSession();
  const { phase } = state;

  const isBusy = phase === 'creating' || phase === 'streaming';
  const studyApiId = useWatch({ control: formMethods.control, name: 'study_api_id' });
  const submitDisabled = isBusy || studyApiId === '';

  const handleSubmit = async (values: StudyHealthCheckFormValues) => {
    const studyApiIdValue = values.study_api_id;
    if (studyApiIdValue === '') {
      return;
    }
    await createSession((signal) =>
      AgentSessionService.createStudyHealthCheckSession({ study_api_id: studyApiIdValue }, signal)
    );
  };

  const handleReset = () => {
    reset();
    formMethods.reset();
  };

  return (
    <div
      className="pb3"
      data-test-component="InVivoStudyHealthCheck"
      data-test-element="container"
      data-testid="invivo-study-health-check"
    >
      <Header
        mainHeaderText="InVivo Study Health Check"
        subHeaderText="Run a health check across a study's measurements, observations, dosing, and alerts"
      />
      <div className={`${styles.layout} pt4`}>
        <div className="ui-card pa4 flex flex-column w-50" data-test-element="form">
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
              <Controller
                control={formMethods.control}
                name="study_api_id"
                rules={{ required: 'Study is required' }}
                render={({ field }) => (
                  <FormField name="study_api_id" label="Study" required testId="invivo-study-health-check__study">
                    <StudyLookup
                      onChange={(study?: StudyInfoV1) => {
                        field.onChange(study?.api_id ?? '');
                      }}
                      isDisabled={isBusy}
                      placeholder="Search studies"
                    />
                  </FormField>
                )}
              />
              <div className={`${styles.actions} mt4`} data-test-element="actions">
                <Button
                  submit
                  primary
                  disabled={submitDisabled}
                  loading={phase === 'creating'}
                  loadingText="Starting"
                  testId="invivo-study-health-check__submit"
                >
                  Run Study Health Check
                </Button>
                {phase === 'streaming' && (
                  <Button outline onClick={cancel} testId="invivo-study-health-check__cancel">
                    Cancel
                  </Button>
                )}
                {(phase === 'completed' || phase === 'failed' || phase === 'cancelled') && (
                  <Button plain onClick={handleReset} testId="invivo-study-health-check__reset">
                    New check
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </div>
        <div className="w-50" data-test-element="response">
          <InVivoStudyHealthCheckResponse
            phase={phase}
            streamEvents={state.streamEvents}
            session={state.session}
            error={state.error}
          />
        </div>
      </div>
    </div>
  );
};
